import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import classes from './tutorSalaries.module.scss'
import { ITutorSalaryData, ITutorSalaryRow, TTutorPaymentType, tutorService } from "../../../entities/tutor";
import { useGlobalLoadingActions } from "../../../entities/globalLoading";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../entities/my";
import { useAppSelector } from "../../../app/store/store";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { Calendar } from "../../../shared/ui/calendar";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";
import { Table } from "../../../shared/ui/table";
import { Copy } from "../../../shared/ui/copy";
import { MyButton } from "../../../shared/ui/button";
import { WorkTgFilter } from "../../workTgFilter";
import { OpenContainer } from "../../../features/openContainer";
import { Link } from "react-router-dom";
import { PERFORMERS_ROUTE } from "../../../app/router/routes";
import { MyInput } from "../../../shared/ui/input";
import lupaImg from "../../../features/searchItems/lib/assets/lupa.png";

const paymentTypeLabels: Record<TTutorPaymentType, string> = {
    card: 'Карта',
    sbp: 'СБП',
    bank_account: 'Реквизиты',
}

const formatCardNumber = (value: string) =>
    value
        .replace(/\D/g, '')
        .slice(0, 19)
        .replace(/(.{4})/g, '$1 ')
        .trim()

export const TutorSalaries: FC = () => {
    const {my} = useAppSelector(state => state.myReducer)
    const canListWorkTgs = hasFeature(my, FEATURES.WORK_TGS.LIST)
    const canCreatePayouts = hasFeature(my, FEATURES.TUTORS.PAYOUTS_CREATE)
    const canCancelPayouts = hasFeature(my, FEATURES.TUTORS.PAYOUTS_CANCEL)
    const canDownloadSalaries = hasFeature(my, FEATURES.FRONT.SALARY)

    const [data, setData] = useState<ITutorSalaryData | null>(null)
    const [workTgIds, setWorkTgIds] = useState<number[]>([])
    const [paymentType, setPaymentType] = useState<TTutorPaymentType>('card')
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
    const [searchValue, setSearchValue] = useState('')

    const {setIsLoading} = useGlobalLoadingActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    const getData = async (from: string, to: string, currentPaymentType: TTutorPaymentType, currentWorkTgIds: number[]) => {
        try{
            setIsLoading(true)
            const salariesRes = await tutorService.getSalaries(from, to, currentPaymentType, currentWorkTgIds)
            setData(salariesRes)
            setSelectedUserIds([])
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении вознаграждений исполнителей', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const onDownload = async () => {
        if(!canDownloadSalaries || !startDate || !endDate) return
        try{
            setIsLoading(true)
            const blob = await tutorService.downloadSalaries(getDateUTC(startDate), getDateUTC(endDate), paymentType, workTgIds, selectedUserIds)
            const a = document.createElement('a')
            const url = URL.createObjectURL(blob)
            a.href = url
            a.download = 'contractor_rewards.xlsx'
            document.body.appendChild(a)
            a.click()
            a.remove()
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при скачивании xlsx по вознаграждениям', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const onPayoutAll = async () => {
        if(!canCreatePayouts || !startDate || !endDate) return

        try{
            setIsLoading(true)
            const from = getDateUTC(startDate)
            const to = getDateUTC(endDate)
            const count = await tutorService.payoutSalaries(from, to, paymentType, workTgIds, selectedUserIds)
            setGlobalMessage({message: `Создано выплат: ${count}`, type: 'ok'})
            await getData(from, to, paymentType, workTgIds)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при создании выплат по текущему фильтру', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const onCancelLatestPayoutAll = async () => {
        if(!canCancelPayouts || !startDate || !endDate) return
        if(!window.confirm('Отменить последнюю массовую выплату? Баланс сотрудников вернется в состояние до выплаты.')){
            return
        }

        try{
            setIsLoading(true)
            const count = await tutorService.cancelLatestSalaryPayouts()
            setGlobalMessage({message: `Отменено выплат: ${count}`, type: 'ok'})
            await getData(getDateUTC(startDate), getDateUTC(endDate), paymentType, workTgIds)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при отмене последней массовой выплаты', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const setDate = (newStartDate: Date | null, newEndDate: Date | null) => {
        setStartDate(newStartDate)
        setEndDate(newEndDate)

        if(newStartDate && newEndDate){
            getData(getDateUTC(newStartDate), getDateUTC(newEndDate), paymentType, workTgIds)
        }
        if(!newStartDate && !newEndDate){
            setData(null)
            setSelectedUserIds([])
        }
    }

    useEffect(() => {
        if(startDate && endDate){
            getData(getDateUTC(startDate), getDateUTC(endDate), paymentType, workTgIds)
        }
    }, [paymentType, workTgIds])

    const getCell = (text: string): ReactNode => {
        if(!text){
            return <span>-</span>
        }

        return (
            <section className={classes.copyCell}>
                <span>{text}</span>
                <Copy text={text} />
            </section>
        )
    }

    const getNameCell = (item: ITutorSalaryRow): ReactNode => (
        <Link className={classes.nameLink} to={`${PERFORMERS_ROUTE.path}/${item.user_id}`}>
            {item.full_name || '-'}
        </Link>
    )

    const selectedUserIdsSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds])
    const filteredItems = useMemo(() => {
        if(!data){
            return []
        }

        const query = searchValue.trim().toLocaleLowerCase('ru-RU')
        if(!query){
            return data.items
        }

        return data.items.filter(item => item.full_name.toLocaleLowerCase('ru-RU').includes(query))
    }, [data, searchValue])
    const allVisibleSelected = filteredItems.length > 0 && filteredItems.every(item => selectedUserIdsSet.has(item.user_id))
    const selectedItems = useMemo(() => {
        if(!data){
            return []
        }

        return data.items.filter(item => selectedUserIdsSet.has(item.user_id))
    }, [data, selectedUserIdsSet])
    const selectedTotal = useMemo(() => {
        const total = selectedItems.reduce((sum, item) => sum + Number(item.salary || 0), 0)
        return total.toLocaleString('ru-RU', {maximumFractionDigits: 2})
    }, [selectedItems])

    const toggleAllVisible = useCallback(() => {
        if(!filteredItems.length){
            return
        }

        const visibleUserIds = new Set(filteredItems.map(item => item.user_id))
        setSelectedUserIds(current => {
            if(allVisibleSelected){
                return current.filter(userId => !visibleUserIds.has(userId))
            }

            return [...new Set([...current, ...visibleUserIds])]
        })
    }, [allVisibleSelected, filteredItems])

    const toggleUser = useCallback((userId: number) => {
        setSelectedUserIds(current =>
            current.includes(userId)
                ? current.filter(id => id !== userId)
                : [...current, userId]
        )
    }, [])

    const getSelectionCell = useCallback((item: ITutorSalaryRow): ReactNode => (
        <input
            className={classes.rowCheckbox}
            type="checkbox"
            checked={selectedUserIdsSet.has(item.user_id)}
            onChange={() => toggleUser(item.user_id)}
            aria-label={`Выбрать выплату для ${item.full_name || 'исполнителя'}`}
        />
    ), [selectedUserIdsSet, toggleUser])

    const titles = useMemo(() => {
        const selectionTitle = (
            <input
                className={classes.rowCheckbox}
                type="checkbox"
                checked={allVisibleSelected}
                disabled={!filteredItems.length}
                onChange={toggleAllVisible}
                aria-label="Выбрать всех исполнителей для выплаты"
                title={allVisibleSelected ? 'Снять выбор со всех' : 'Выбрать всех'}
            />
        )

        const metricTitles = ['Занятий', 'Часов', 'Штрафы', 'Бонусы']

        switch(paymentType){
            case 'sbp':
                return [selectionTitle, 'ФИО', 'Номер тел', 'Банк', ...metricTitles, 'Сумма вознаграждения', 'Баланс']
            case 'bank_account':
                return [selectionTitle, 'ФИО', 'Номер счета', 'БИК', 'ИНН', ...metricTitles, 'Сумма вознаграждения', 'Баланс']
            default:
                return [selectionTitle, 'ФИО', 'Номер карты', ...metricTitles, 'Сумма вознаграждения', 'Баланс']
        }
    }, [allVisibleSelected, filteredItems.length, paymentType, toggleAllVisible])

    const rows = useMemo(() => {
        if(!data){
            return []
        }

        return filteredItems.map((item: ITutorSalaryRow) => {
            const metricCells = [
                getCell(String(item.lessons_count)),
                getCell(item.hours),
                getCell(item.penalties),
                getCell(item.bonuses),
            ]

            if(paymentType === 'sbp'){
                return [
                    getSelectionCell(item),
                    getNameCell(item),
                    getCell(item.phone_number),
                    getCell(item.bank_name),
                    ...metricCells,
                    getCell(item.salary),
                    getCell(item.balance),
                ]
            }

            if(paymentType === 'bank_account'){
                return [
                    getSelectionCell(item),
                    getNameCell(item),
                    getCell(item.account_number),
                    getCell(item.bik),
                    getCell(item.inn),
                    ...metricCells,
                    getCell(item.salary),
                    getCell(item.balance),
                ]
            }

            return [
                getSelectionCell(item),
                getNameCell(item),
                getCell(formatCardNumber(item.card_number)),
                ...metricCells,
                getCell(item.salary),
                getCell(item.balance),
            ]
        })
    }, [data, filteredItems, getSelectionCell, paymentType])

    return (
        <OpenContainer
            title="Вознаграждение исполнителей"
            allowOverflow
            icon={<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10H21M16 14H18" /></svg>}
        >
            <section className={classes.container}>
                <section className={classes.controls}>
                <Calendar onDateRangeSelect={setDate} />
                <section className={classes.types}>
                    {(Object.keys(paymentTypeLabels) as TTutorPaymentType[]).map(type =>
                        <button
                            key={type}
                            onClick={() => setPaymentType(type)}
                            className={classes.typeButton + (paymentType === type ? ` ${classes.active}` : '')}
                        >
                            {paymentTypeLabels[type]}
                        </button>    
                    )}
                </section>
                {canListWorkTgs && <section className={classes.selectedWorkTgs}>
                    <WorkTgFilter setWorkTgIds={setWorkTgIds} />
                </section>}
                {canCreatePayouts && (
                    <section className={classes.download}>
                        <MyButton onClick={onPayoutAll} disabled={!selectedUserIds.length}>
                            Выплатить выбранным
                        </MyButton>
                    </section>
                )}
                {canCancelPayouts && (
                    <section className={classes.download}>
                        <MyButton onClick={onCancelLatestPayoutAll}>
                            Отменить последнюю выплату
                        </MyButton>
                    </section>
                )}
                {canDownloadSalaries && (
                    <section className={classes.download}>
                        <MyButton onClick={onDownload}>
                            Скачать xlsx
                        </MyButton>
                    </section>
                )}
            </section>
                {
                    data
                        &&
                    <>
                        <section className={classes.total}>
                            Общая сумма вознаграждения: {data.total_salary} ₽
                        </section>
                        <section className={classes.selectedSummary}>
                            Выбрано исполнителей: {selectedItems.length} из {data.items.length}. К выплате: {selectedTotal} ₽
                        </section>
                        <section className={classes.search}>
                            <MyInput
                                value={searchValue}
                                setValue={setSearchValue}
                                placeholder="Поиск по ФИО"
                                iconSrc={lupaImg}
                            />
                        </section>
                        <section className={classes.wrapTable}>
                            <Table titles={titles} rows={rows} />
                        </section>
                    </>
                }
            </section>
        </OpenContainer>
    )
}
